import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Resource, type PermissionString, Action } from './permissions.js';
import { HttpClient } from '../api/api.client.js';

async function fetchPermissions(): Promise<PermissionString[]> {
  return HttpClient.getInstance()
    .get('/users/me/permissions')
    .then((res) => res.data);
}

type FormattedPermissionsType = {
  [key in Resource]?: Action[]
};

type ResponsePermissions = {
  [key in `can${keyof typeof Action}${keyof typeof Resource}`]: boolean;
};

interface UseCheckPermissionsResponse extends ResponsePermissions {
  isLoading: boolean;
}

export default function useCheckPermissions(): UseCheckPermissionsResponse {
  const { data = [], isLoading } = useQuery<PermissionString[]>(['permissions'], () => fetchPermissions(), {
    cacheTime: 12 * 60 * 60 * 1000,
    staleTime: 12 * 60 * 60 * 1000,
  });
  const permissions = useMemo(() => {
    const result: Partial<ResponsePermissions> = {};
    const formattedPermissions: FormattedPermissionsType = {};
    data.forEach((permission: PermissionString) => {
      const [resource, action] = permission.split(':');
      if (formattedPermissions[resource as Resource]?.length) {
        formattedPermissions[resource as Resource]?.push(action as Action);
      } else {
        formattedPermissions[resource as Resource] = [action as Action];
      }
    });
    const actions = Object.entries(Action);
    Object.entries(Resource).forEach(([resourceKey, resource]) => {
      actions.forEach(([actionKey, action]) => {
        result[`can${actionKey as keyof typeof Action}${resourceKey as keyof typeof Resource}`] = !!formattedPermissions[resource]?.includes(action);
      });
    });
    return result as ResponsePermissions;
  }, [data]);
  return {
    isLoading,
    ...permissions,
  };
}
